import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { HTTPResponse } from '@mavles/shared';
import { Account } from '@models/user/account';
import { IRole, IUser, User } from '@models/user/user';
import { APP_CONFIG } from '@mavles/shared';

@Injectable({ providedIn: 'root' })
export class AccountApiService {
  private appConfig = inject(APP_CONFIG);

  protected BASE_URL = `${this.appConfig.apiUrl}account`;
  protected AUTH_URL = `${this.appConfig.apiUrl}auth`;
  protected BASE_URL_CONTACT = `${this.appConfig.apiUrl}contacts`;
  protected BASE_URL_ADMIN = `${this.appConfig.apiUrl}admin`;

  private accountSubject = new BehaviorSubject<Account | null>(null);
  public account = this.accountSubject.asObservable();

  public currentURL!: string;

  protected router = inject(Router);
  protected http = inject(HttpClient);

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentURL = this.router.url;
      }
    });
  }

  // ===================== BASIC =====================

  getUser(): Observable<HTTPResponse<User>> {
    return this.http.get<HTTPResponse<User>>(`${this.AUTH_URL}/me`);
  }

  getUsers(): Observable<HTTPResponse<User[]>> {
    return this.http.get<HTTPResponse<User[]>>(`${this.BASE_URL}/users`);
  }

  register(account: Account): Observable<HTTPResponse<Account>> {
    return this.http.post<HTTPResponse<Account>>(
      `${this.BASE_URL}/register`,
      account,
    );
  }

  verifyEmail(token: string): Observable<HTTPResponse<void>> {
    return this.http.post<HTTPResponse<void>>(`${this.AUTH_URL}/verify-email`, {
      token,
    });
  }

  forgotPassword(email: string): Observable<HTTPResponse<void>> {
    return this.http.post<HTTPResponse<void>>(
      `${this.AUTH_URL}/forgot-password`,
      { email },
    );
  }

  validateResetToken(token: string): Observable<HTTPResponse<void>> {
    return this.http.post<HTTPResponse<void>>(
      `${this.AUTH_URL}/validate-reset-token`,
      { token },
    );
  }

  resetPassword(
    token: string,
    password: string,
    confirmPassword: string,
  ): Observable<HTTPResponse<void>> {
    return this.http.post<HTTPResponse<void>>(
      `${this.AUTH_URL}/reset-password`,
      {
        token,
        password,
        confirmPassword,
      },
    );
  }

  // ===================== AUTH =====================

  login(
    email: string,
    password: string,
  ): Observable<HTTPResponse<{ user: User; token: string }>> {
    return this.http.post<HTTPResponse<{ user: User; token: string }>>(
      `${this.AUTH_URL}/login`,
      { email, password },
    );
  }

  logout(): void {
    this.http.post(`${this.AUTH_URL}/revoke-token`, {}).subscribe({
      complete: () => this.router.navigate(['/account/login']),
    });
  }

  refreshToken(): Observable<HTTPResponse<{ token: string }>> {
    return this.http.post<HTTPResponse<{ token: string }>>(
      `${this.AUTH_URL}/refresh-token`,
      {},
    );
  }

  // ===================== ACCOUNT =====================

  getAll(): Observable<Account[]> {
    return this.http.get<Account[]>(`${this.BASE_URL}`);
  }

  getById(id: string): Observable<Account> {
    return this.http.get<Account>(`${this.BASE_URL}/${id}`);
  }

  create(params: any): Observable<HTTPResponse<Account>> {
    return this.http.post<HTTPResponse<Account>>(`${this.BASE_URL}`, params);
  }

  update(id: string, params: any): Observable<HTTPResponse<Account>> {
    return this.http.put<HTTPResponse<Account>>(
      `${this.BASE_URL}/${id}`,
      params,
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.BASE_URL}/${id}`);
  }

  // ===================== ADMIN =====================

  getRoles(): Observable<HTTPResponse<IRole[]>> {
    return this.http.get<HTTPResponse<IRole[]>>(
      `${this.BASE_URL_ADMIN}/user/roles`,
    );
  }

  getUserById(id: number): Observable<HTTPResponse<IUser>> {
    return this.http.get<HTTPResponse<IUser>>(
      `${this.BASE_URL_ADMIN}/user/${id}`,
    );
  }

  createUser(user: any): Observable<HTTPResponse<IUser>> {
    return this.http.post<HTTPResponse<IUser>>(
      `${this.BASE_URL_ADMIN}/user`,
      user,
    );
  }

  updateUser(id: number, user: any): Observable<HTTPResponse<IUser>> {
    return this.http.put<HTTPResponse<IUser>>(
      `${this.BASE_URL_ADMIN}/user/${id}`,
      user,
    );
  }
}
